import type { CrmMessages } from "@/components/atomic-crm/providers/commons/englishCrmMessages";

/**
 * Full Dutch copy for Atomic CRM strings. PalmTree wording in palmtree.ts
 * is merged on top and wins where both define a key.
 */
export const dutchCrmCatalog = {
  resources: {
    companies: {
      name: "Bedrijf |||| Bedrijven",
      forcedCaseName: "Bedrijf",
      fields: {
        name: "Bedrijfsnaam",
        website: "Website",
        linkedin_url: "LinkedIn-URL",
        phone_number: "Telefoonnummer",
        created_at: "Aangemaakt op",
        nb_contacts: "Aantal contacten",
        revenue: "Omzet",
        sector: "Sector",
        size: "Grootte",
        tax_identifier: "Fiscaal nummer",
        address: "Adres",
        city: "Plaats",
        zipcode: "Postcode",
        state_abbr: "Provincie",
        country: "Land",
        description: "Beschrijving",
        context_links: "Contextlinks",
        sales_id: "Accountmanager",
      },
      empty: {
        description: "Je bedrijvenlijst lijkt leeg.",
        title: "Geen bedrijven gevonden",
      },
      field_categories: {
        contact: "Contact",
        additional_info: "Extra informatie",
        address: "Adres",
        context: "Context",
      },
      action: {
        create: "Bedrijf aanmaken",
        edit: "Bedrijf bewerken",
        new: "Nieuw bedrijf",
        show: "Bedrijf tonen",
      },
      added_on: "Toegevoegd op %{date}",
      followed_by: "Gevolgd door %{name}",
      followed_by_you: "Gevolgd door jou",
      no_contacts: "Geen contact",
      nb_contacts: "%{smart_count} contact |||| %{smart_count} contacten",
      nb_deals: "%{smart_count} deal |||| %{smart_count} deals",
      sizes: {
        one_employee: "1 medewerker",
        two_to_nine_employees: "2-9 medewerkers",
        ten_to_forty_nine_employees: "10-49 medewerkers",
        fifty_to_two_hundred_forty_nine_employees: "50-249 medewerkers",
        two_hundred_fifty_or_more_employees: "250 of meer medewerkers",
      },
      autocomplete: {
        create_error: "Er ging iets mis bij het aanmaken van het bedrijf",
        create_item: "%{item} aanmaken",
        create_label: "Begin met typen om een nieuw bedrijf aan te maken",
      },
      filters: {
        only_mine: "Alleen bedrijven die ik beheer",
      },
    },
    contacts: {
      name: "Contact |||| Contacten",
      forcedCaseName: "Contact",
      field_categories: {
        background_info: "Achtergrond",
        identity: "Identiteit",
        misc: "Overig",
        personal_info: "Persoonlijke gegevens",
        position: "Functie",
      },
      fields: {
        first_name: "Voornaam",
        last_name: "Achternaam",
        last_seen: "Laatst gezien",
        title: "Rol",
        type: "Type",
        company_id: "Bedrijf",
        email_jsonb: "Email",
        email: "Email",
        phone_jsonb: "Tel",
        phone_number: "Tel",
        linkedin_url: "LinkedIn-URL",
        background: "Achtergrond (bio, hoe je elkaar kent, enz.)",
        has_newsletter: "Nieuwsbrief",
        sales_id: "Accountmanager",
      },
      status: {
        none: "Geen status",
      },
      type_change_hint:
        "Wijzigen verplaatst dit contact naar Contactpersonen of Bedrijven.",
      action: {
        add: "Contact toevoegen",
        add_first: "Voeg je eerste contact toe",
        create: "Contact aanmaken",
        edit: "Contact bewerken",
        export_vcard: "Exporteren naar vCard",
        new: "Nieuw contact",
        show: "Contact tonen",
      },
      background: {
        last_activity_on: "Laatste activiteit op %{date}",
        added_on: "Toegevoegd op %{date}",
        followed_by: "Gevolgd door %{name}",
        followed_by_you: "Gevolgd door jou",
        status_none: "Geen",
      },
      position_at: "%{title} bij",
      position_at_company: "%{title} bij %{company}",
      empty: {
        description: "Je contactenlijst lijkt leeg.",
        title: "Geen contacten gevonden",
      },
      import: {
        title: "Contacten importeren",
        button: "CSV importeren",
        complete:
          "Import afgerond. %{importCount} contacten geïmporteerd, met %{errorCount} fouten",
        progress:
          "%{importCount} / %{rowCount} contacten geïmporteerd, met %{errorCount} fouten.",
        error:
          "Dit bestand kon niet worden geïmporteerd. Controleer of het een geldig CSV-bestand is.",
        imported: "Geïmporteerd",
        remaining_time: "Geschatte resterende tijd:",
        running: "De import loopt. Sluit dit tabblad niet.",
        sample_download: "Voorbeeld-CSV downloaden",
        sample_hint:
          "Dit is een voorbeeld-CSV dat je als sjabloon kunt gebruiken",
        stop: "Import stoppen",
        csv_file: "CSV-bestand",
        contacts_label: "contact |||| contacten",
      },
      inputs: {
        genders: {
          male: "Hij/hem",
          female: "Zij/haar",
          nonbinary: "Hen/hun",
        },
        personal_info_types: {
          work: "Werk",
          home: "Thuis",
          other: "Anders",
        },
      },
      list: {
        error_loading: "Fout bij het laden van contacten",
      },
      bulk_tag: {
        action: "Tag",
        back: "Terug naar tags",
        create_description:
          "Maak een nieuwe tag en pas die toe op de geselecteerde contacten.",
        description:
          "Kies een bestaande tag of maak een nieuwe voor de geselecteerde contacten.",
        empty:
          "Nog geen tags. Maak er een om de geselecteerde contacten te taggen.",
        error: "Tag toevoegen aan contacten is mislukt",
        noop: "De geselecteerde contacten hebben deze tag al",
        success:
          "Tag toegevoegd aan %{smart_count} contact |||| Tag toegevoegd aan %{smart_count} contacten",
        title: "Tag toevoegen aan contacten",
      },
      merge: {
        action: "Samenvoegen met een ander contact",
        confirm: "Contacten samenvoegen",
        current_contact: "Huidig contact (wordt verwijderd)",
        description: "Voeg dit contact samen met een ander contact.",
        error: "Contacten samenvoegen is mislukt",
        merging: "Samenvoegen...",
        no_additional_data: "Geen extra gegevens om samen te voegen",
        select_target: "Kies een contact om mee samen te voegen",
        success: "Contacten samengevoegd",
        target_contact: "Doelcontact (blijft behouden)",
        title: "Contact samenvoegen",
        warning_description:
          "Alle gegevens gaan naar het tweede contact. Dit kan niet ongedaan worden gemaakt.",
        warning_title: "Let op: deze actie verwijdert gegevens",
        what_will_be_merged: "Wat wordt samengevoegd:",
      },
      filters: {
        before_last_month: "Vóór vorige maand",
        before_this_month: "Vóór deze maand",
        before_this_week: "Vóór deze week",
        managed_by_me: "Door mij beheerd",
        search: "Zoek naam, bedrijf...",
        this_week: "Deze week",
        today: "Vandaag",
        tags: "Tags",
        tasks: "Taken",
      },
      hot: {
        empty_change_status:
          'Wijzig de status van een contact door een notitie toe te voegen en op "opties tonen" te klikken.',
        empty_hint: 'Contacten met de status "hot" verschijnen hier.',
        title: "Hot contacts",
      },
    },
    deals: {
      name: "Deal |||| Deals",
      fields: {
        name: "Naam",
        description: "Beschrijving",
        company_id: "Bedrijf",
        contact_ids: "Contacten",
        category: "Categorie",
        amount: "Budget",
        expected_closing_date: "Verwachte sluitingsdatum",
        stage: "Fase",
      },
      action: {
        back_to_deal: "Terug naar deal",
        create: "Deal aanmaken",
        new: "Nieuwe deal",
      },
      field_categories: {
        misc: "Overig",
      },
      archived: {
        action: "Archiveren",
        error: "Fout: deal niet gearchiveerd",
        list_title: "Gearchiveerde deals",
        success: "Deal gearchiveerd",
        title: "Gearchiveerde deal",
        view: "Gearchiveerde deals bekijken",
      },
      inputs: {
        linked_to: "Gekoppeld aan",
      },
      unarchived: {
        action: "Terug naar het bord",
        error: "Fout: deal niet teruggezet",
        success: "Deal teruggezet",
      },
      updated: "Deal bijgewerkt",
      empty: {
        before_create: "voordat je een deal aanmaakt.",
        description: "Je deallijst lijkt leeg.",
        title: "Geen deals gevonden",
      },
      invalid_date: "Ongeldige datum",
    },
    notes: {
      name: "Notitie |||| Notities",
      forcedCaseName: "Notitie",
      fields: {
        status: "Status",
        date: "Datum",
        attachments: "Bijlagen",
        contact_id: "Contact",
        deal_id: "Deal",
      },
      action: {
        add: "Notitie toevoegen",
        add_first: "Voeg je eerste notitie toe",
        delete: "Notitie verwijderen",
        edit: "Notitie bewerken",
        update: "Notitie bijwerken",
        add_this: "Notitie toevoegen",
      },
      sheet: {
        create: "Notitie aanmaken",
        create_for: "Notitie aanmaken voor %{name}",
        edit: "Notitie bewerken",
        edit_for: "Notitie bewerken voor %{name}",
      },
      deleted: "Notitie verwijderd",
      empty: "Nog geen notities",
      author_added: "%{name} heeft een notitie toegevoegd",
      you_added: "Je hebt een notitie toegevoegd",
      me: "Ik",
      list: {
        error_loading: "Fout bij het laden van notities",
      },
      note_for_contact: "Notitie voor %{name}",
      stepper: {
        hint: "Ga naar een contact en voeg een notitie toe",
      },
      added: "Notitie toegevoegd",
      inputs: {
        add_note: "Nieuwe notitie...",
        options_hint: "(bestanden toevoegen of details wijzigen)",
        show_options: "Opties tonen",
      },
      actions: {
        attach_document: "Document toevoegen",
      },
      validation: {
        note_or_attachment_required: "Een notitie of een bijlage is verplicht",
      },
    },
    sales: {
      name: "Gebruiker |||| Gebruikers",
      fields: {
        first_name: "Voornaam",
        last_name: "Achternaam",
        email: "E-mail",
        administrator: "Beheerder",
        disabled: "Uitgeschakeld",
      },
      create: {
        error: "Er ging iets mis bij het aanmaken van de gebruiker.",
        success:
          "Gebruiker aangemaakt. Die ontvangt binnenkort een e-mail om een wachtwoord te kiezen.",
        title: "Nieuwe gebruiker aanmaken",
      },
      edit: {
        error: "Er ging iets mis. Probeer het opnieuw.",
        record_not_found: "Record niet gevonden",
        success: "Gebruiker bijgewerkt",
        title: "%{name} bewerken",
      },
      action: {
        new: "Nieuwe gebruiker",
      },
    },
    tasks: {
      name: "Actie |||| Acties",
      forcedCaseName: "Actie",
      fields: {
        text: "Actie",
        due_date: "Due datum",
        type: "Type",
        contact_id: "Contact",
        due_short: "deadline",
      },
      action: {
        add: "Actie toevoegen",
        create: "Actie aanmaken",
        edit: "Actie bewerken",
      },
      actions: {
        postpone_next_week: "Uitstellen naar volgende week",
        postpone_tomorrow: "Uitstellen naar morgen",
        title: "actie-opties",
      },
      added: "Actie toegevoegd",
      deleted: "Actie verwijderd",
      dialog: {
        create: "Actie aanmaken",
        create_for: "Actie aanmaken voor %{name}",
      },
      sheet: {
        edit: "Actie bewerken",
        edit_for: "Actie bewerken voor %{name}",
      },
      empty: "Nog geen acties",
      empty_list_hint: "Acties bij je contacten verschijnen hier.",
      filters: {
        later: "Later",
        overdue: "Te laat",
        this_week: "Deze week",
        today: "Vandaag",
        tomorrow: "Morgen",
        with_pending: "Met openstaande acties",
      },
      regarding_contact: "(Betreft: %{name})",
      updated: "Actie bijgewerkt",
    },
    tags: {
      name: "Tag |||| Tags",
      action: {
        add: "Tag toevoegen",
        create: "Nieuwe tag aanmaken",
      },
      dialog: {
        color: "Kleur",
        create_title: "Nieuwe tag aanmaken",
        edit_title: "Tag bewerken",
        name_label: "Tagnaam",
        name_placeholder: "Vul een tagnaam in",
      },
    },
  },
  crm: {
    errors: {
      offline: "Geen verbinding",
      offline_stale: "Geen verbinding. Deze gegevens kunnen verouderd zijn.",
      network:
        "Geen verbinding met de server. Je wijziging is niet opgeslagen. Probeer het opnieuw.",
      forbidden: "Je hebt geen rechten voor deze actie.",
      server: "Er ging iets mis op de server. Probeer het later opnieuw.",
      generic: "Er ging iets mis. Probeer het opnieuw.",
      retry: "Opnieuw proberen",
    },
    action: {
      reset_password: "Wachtwoord resetten",
    },
    auth: {
      first_name: "Voornaam",
      last_name: "Achternaam",
      confirm_password: "Bevestig wachtwoord",
      confirmation_required:
        "Open de link die we je net per e-mail hebben gestuurd om je account te bevestigen.",
      recovery_email_sent:
        "Als je een account hebt, ontvang je zo een e-mail om je wachtwoord te herstellen.",
      sign_in_failed: "Inloggen is mislukt.",
      sign_in_google_workspace: "Inloggen met Google Workspace",
      invalid_credentials: "E-mail of wachtwoord klopt niet.",
      generic_error: "Er ging iets mis. Probeer het opnieuw.",
      secure_access: "beveiligde toegang",
      placeholders: {
        email: "naam@bedrijf.nl",
      },
      login: {
        title: "Welkom terug",
        subtitle: "Log in om verder te gaan in PalmTree Hub",
        no_account: "Nog geen account?",
      },
      signup: {
        create_account: "Account aanmaken",
        create_first_user:
          "Maak het eerste gebruikersaccount aan om de installatie af te ronden.",
        creating: "Aanmaken...",
        initial_user_created: "Eerste gebruiker aangemaakt",
        email_taken: "Dit e-mailadres is al in gebruik.",
      },
      welcome_title: "Welkom bij PalmTree Hub",
    },
    common: {
      activity: "Activiteit",
      added: "toegevoegd",
      details: "Details",
      last_activity_with_date: "laatste activiteit %{date}",
      load_more: "Meer laden",
      misc: "Overig",
      past: "Verleden",
      read_more: "Lees meer",
      retry: "Opnieuw",
      show_less: "Minder tonen",
      copied: "Gekopieerd!",
      copy: "Kopiëren",
      loading: "Laden...",
      me: "Ik",
      task_count: "%{smart_count} actie |||| %{smart_count} acties",
    },
    changelog: {
      title: "Wijzigingslog",
    },
    activity: {
      added_company: "%{name} heeft een bedrijf toegevoegd",
      you_added_company: "Je hebt een bedrijf toegevoegd",
      added_contact: "%{name} heeft toegevoegd",
      you_added_contact: "Je hebt toegevoegd",
      added_note: "%{name} heeft een notitie toegevoegd over",
      you_added_note: "Je hebt een notitie toegevoegd over",
      added_note_about_deal: "%{name} heeft een notitie toegevoegd over deal",
      you_added_note_about_deal: "Je hebt een notitie toegevoegd over deal",
      added_deal: "%{name} heeft een deal toegevoegd",
      you_added_deal: "Je hebt een deal toegevoegd",
      at_company: "bij",
      to: "aan",
      load_more: "Meer activiteit laden",
    },
    dashboard: {
      deals_chart: "Verwachte dealomzet",
      deals_pipeline: "Dealpijplijn",
      latest_activity: "Laatste activiteit",
      latest_activity_error: "Fout bij het laden van de laatste activiteit",
      latest_notes: "Mijn laatste notities",
      latest_notes_added_ago: "%{timeAgo} toegevoegd",
      stepper: {
        install: "Atomic CRM installeren",
        progress: "%{step}/3 klaar",
        whats_next: "Wat nu?",
      },
      upcoming_tasks: "Komende acties",
    },
    header: {
      import_data: "Gegevens importeren",
    },
    image_editor: {
      change: "Wijzigen",
      drop_hint: "Sleep een bestand hierheen of klik om het te selecteren.",
      editable_content: "Bewerkbare inhoud",
      title: "Afbeelding uploaden en bijsnijden",
      update_image: "Afbeelding bijwerken",
    },
    import: {
      action: {
        download_error_report: "Foutrapport downloaden",
        import: "Importeren",
        import_another: "Nog een bestand importeren",
      },
      error: {
        unable: "Dit bestand kan niet worden geïmporteerd.",
      },
      idle: {
        description_1:
          "Je kunt sales, bedrijven, contacten, notities en acties importeren.",
        description_2:
          "De gegevens moeten in een JSON-bestand staan dat overeenkomt met dit voorbeeld:",
      },
      status: {
        all_success: "Alle records zijn geïmporteerd.",
        complete: "Import afgerond.",
        failed: "Mislukt",
        imported: "Geïmporteerd",
        in_progress: "Import bezig. Blijf op deze pagina.",
        some_failed: "Sommige records zijn niet geïmporteerd.",
        table_caption: "Importstatus",
      },
      title: "Gegevens importeren",
    },
    settings: {
      about: "Over",
      companies: {
        sectors: "Sectoren",
      },
      dark_mode_logo: "Logo donkere modus",
      deals: {
        categories: "Categorieën",
        currency: "Valuta",
        pipeline_help: "Kies welke dealfases meetellen als pijplijn.",
        pipeline_statuses: "Pijplijnstatussen",
        stages: "Fases",
      },
      light_mode_logo: "Logo lichte modus",
      notes: {
        statuses: "Statussen",
      },
      reset_defaults: "Standaardwaarden herstellen",
      save_error: "Configuratie opslaan is mislukt",
      saved: "Configuratie opgeslagen",
      saving: "Opslaan...",
      tasks: {
        types: "Types",
      },
      preferences: "Voorkeuren",
      title: "Instellingen",
      app_title: "App-titel",
      sections: {
        branding: "Huisstijl",
      },
      validation: {
        duplicate: "Dubbele %{display_name}: %{items}",
        in_use:
          "Kan %{display_name} niet verwijderen die nog door deals worden gebruikt: %{items}",
        validating: "Controleren\u2026",
        entities: {
          categories: "categorieën",
          stages: "fases",
        },
      },
    },
    theme: {
      dark: "Donker",
      label: "Thema",
      light: "Licht",
      system: "Systeem",
    },
    language: "Taal",
    navigation: {
      label: "CRM-navigatie",
    },
    profile: {
      inbound: {
        description:
          "Je kunt e-mails sturen naar het inkomende adres van je server, bijvoorbeeld door het toe te voegen aan het veld %{field}. Atomic CRM verwerkt de e-mails en voegt notities toe aan de bijbehorende contacten.",
        title: "Inkomende e-mail",
      },
      mcp: {
        title: "MCP-server",
        description:
          "Gebruik deze URL om je AI-assistent via het Model Context Protocol (MCP) te koppelen aan je CRM-gegevens.",
      },
      password: {
        change: "Wachtwoord wijzigen",
      },
      password_reset_sent:
        "Er is een e-mail om je wachtwoord te resetten gestuurd naar je e-mailadres",
      record_not_found: "Record niet gevonden",
      title: "Profiel",
      updated: "Je profiel is bijgewerkt",
      update_error: "Er ging iets mis. Probeer het opnieuw",
    },
    validation: {
      invalid_url: "Moet een geldige URL zijn",
      invalid_linkedin_url: "De URL moet van linkedin.com zijn",
    },
  },
} satisfies CrmMessages;
